import { useEffect, useRef } from "react";
import { Button } from "@arco-design/web-react";

import { IntakeShell } from "./components/IntakeShell";
import { IntakeFooterNav } from "./components/IntakeFooterNav";
import { IntakeCameraPreview } from "./components/IntakeCameraPreview";
import { useIntakeCopy } from "./hooks/useIntakeCopy";

type Props = {
  stream: MediaStream | null;
  error: "denied" | "unavailable" | null;
  videoActive: boolean;
  audioActive: boolean;
  isLoading: boolean;
  onStart: () => void;
  onContinue: () => void;
  onRetry: () => void;
  onBack: () => void;
};

export function IntakeCameraSetup({
  stream,
  error,
  videoActive,
  audioActive,
  isLoading,
  onStart,
  onContinue,
  onRetry,
  onBack,
}: Props) {
  const { t, progressLabel } = useIntakeCopy();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasRequestedMedia = useRef(false);

  useEffect(() => {
    if (hasRequestedMedia.current) return;
    hasRequestedMedia.current = true;
    onStart();
  }, [onStart]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && stream) {
      video.srcObject = stream;
      void video.play().catch(() => {});
    }
  }, [stream]);

  const canContinue = videoActive && audioActive && !error;

  if (error) {
    return (
      <IntakeShell
        progressStep={2}
        progressTotal={5}
        progressLabel={progressLabel(2, 5)}
      >
        <div className="intake-blocked">
          <h2 className="intake-flow__headline">{t("intake.camera.error_title")}</h2>
          <p className="intake-flow__subtext">{t("intake.camera.error_body")}</p>
          <Button
            className="intake-flow__cta"
            size="large"
            onClick={() => {
              hasRequestedMedia.current = false;
              onRetry();
            }}
          >
            {t("intake.camera.try_again")}
          </Button>
        </div>
      </IntakeShell>
    );
  }

  return (
    <IntakeShell
      progressStep={2}
      progressTotal={5}
      progressLabel={progressLabel(2, 5)}
      footer={
        <IntakeFooterNav
          onBack={onBack}
          backLabel={t("intake.back")}
          primaryLabel={t("intake.next")}
          onPrimary={onContinue}
          primaryDisabled={!canContinue || isLoading}
        />
      }
    >
      <h1 className="intake-flow__headline">{t("intake.camera.headline")}</h1>
      <p className="intake-flow__subtext intake-flow__subtext--center">
        {t("intake.camera.subtext")}
      </p>

      <ul className="intake-checklist intake-checklist--inline">
        <li>
          <span
            className={`intake-checklist__mark${
              audioActive ? "" : " intake-checklist__mark--pending"
            }`}
          >
            {audioActive ? "✓" : "○"}
          </span>
          {t("intake.camera.audio_label")}
        </li>
        <li>
          <span
            className={`intake-checklist__mark${
              videoActive ? "" : " intake-checklist__mark--pending"
            }`}
          >
            {videoActive ? "✓" : "○"}
          </span>
          {t("intake.camera.mic_label")}
        </li>
      </ul>

      <IntakeCameraPreview
        videoRef={videoRef}
        stream={stream}
        showFaceGuide={Boolean(stream)}
        placeholder={
          isLoading ? t("intake.camera.connecting") : undefined
        }
        onActivate={onStart}
        activateLabel={t("intake.camera.turn_on_camera")}
      />
    </IntakeShell>
  );
}
